import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Datatable } from 'ui';
import { getSearchableRegions, getSearchbleIndustries } from 'selectors/common';
import { useSelectedTableRows, useTable } from 'shared/hooks';
import { usePortfolioModals } from 'shared/hooks/usePortfolioModals/usePortfolioModals';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { fetchInstruments } from 'services';
import { exportCoveredBonds } from 'services/export';
import { ItemType, SelectedEntities } from 'shared/types/portfolios';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import {
  buildCountryFilter,
  getInitialSearchResultsModalFilters,
} from 'shared/functions/searchFilters/searchFilters';
import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { CreateNewPortfolioModal } from 'modules/common/Modals';
import { useDispatch, useSelector } from 'react-redux';
import { parseDemandType } from 'shared/parsers';
import { useInstrumentRenderers } from 'modules/creditRating/hooks/useInstrumentRenderers';
import { useInstrumentsRatingRequest } from 'modules/creditRating/hooks/useInstrumentsRatingRequest';
import InstrumentsFiltersModal from 'modules/common/Table/InstrumentsTable/InstrumentsFiltersModal';
import { CoveredBondsItem } from './types';
import { ratingTypes } from '../../../shared/constants/ratingTypes';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.grey[50],
    '& thead > tr': {
      whiteSpace: 'pre',
    },
  },
}));

type CoveredBondsListParams = {
  countryCode: string;
  countryCodes: Array<string>;
};

type ShouldRefresh = {
  value: boolean;
  reason: string | null;
};

export const CoveredBondsList: FC<CoveredBondsListParams> = ({ countryCode, countryCodes }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { singleSelect, multiSelect, currentSelect, selectedAll, onSelectAll, onSelectedChange } =
    useSelectedTableRows(ItemType.ISIN);

  const [updating, setUpdating] = useState(false);
  const { createPortfolio, addToPortfolio } = usePortfolioModals();
  const { error } = useSnackbar();
  const [total, setTotal] = useState<number>(0);
  const [coveredBonds, setCoveredBonds] = useState<Array<CoveredBondsItem>>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [shouldRefresh, setShouldRefresh] = useState<ShouldRefresh>({
    value: false,
    reason: null,
  });

  const { data: regions } = useSelector(getSearchableRegions);
  const industries = useSelector(getSearchbleIndustries);

  const [filters, setFilters] = useState({} as { [key: string]: any });

  const [availableCountries, setAvailableCountries] = useState([]);
  const [initialFilters, setInitialFilters] = useState({});
  const [initialModalFilters, setInitialModalFilters] = useState({});

  const [instrumentsFiltersModalOpen, setInstrumentsFiltersModalOpen] = useState(false);
  const toggleInstrumentsFiltersModal = () =>
    setInstrumentsFiltersModalOpen(!instrumentsFiltersModalOpen);

  const onRequested = () => {
    setShouldRefresh({ value: true, reason: refreshReasons.PRODUCT_REQUESTED });
    dispatch(refreshTable);
  };

  const { requestLoading, requestRating } = useInstrumentsRatingRequest({ onRequested });
  const {
    nameRenderer,
    ratingRenderer,
    countryRenderer,
    entityRenderer,
    valueRenderer,
    typeRenderer,
    protectedRenderer,
  } = useInstrumentRenderers();

  const prepareTableData = (coveredBondsList: Array<CoveredBondsItem>) =>
    coveredBondsList.map((item: CoveredBondsItem) => {
      const isBlurred = item.ratingType === 'ON_DEMAND_OPEN_SUBSCRIPTION' && !item.hasLicence;

      return [
        [item.isin, isBlurred],
        [item.id, item.name, isBlurred],
        [item.issuer?.id, item.issuer?.name, isBlurred],
        [item.issuer?.countryCode, isBlurred],
        [item.seniorityType, isBlurred],
        [item.issueVolume, item.currencyCode, isBlurred],
        item.ratingType,
        [parseDemandType(item.rating?.demandType), isBlurred],
        [
          item.id,
          null,
          item.rating?.rating,
          item.rating?.outlook,
          item.hasLicence,
          null,
          null,
          null,
          null,
          item.rating?.demandType,
          isBlurred,
          item.rating?.id,
          item.rating?.notchSuffix,
          item.rating?.preliminaryFlag,
          item.ratingType,
        ],
        [item.rating?.outlook, isBlurred],
      ];
    });

  const fetchCoveredBondsList = async (
    page: number,
    limit: number,
    sort?: { sort: string; dir: string },
  ) => {
    if (!filters.coveredBond) return;
    setUpdating(true);
    const resp = await fetchInstruments({
      query: undefined,
      page: page + 1 || 1,
      limit,
      sort: { sortColumn: sort?.sort, direction: sort?.dir },
      filter: filters,
    });
    setCoveredBonds(resp.data as Array<CoveredBondsItem>);
    setTotal(resp.total);
    setUpdating(false);
  };

  const copyInstrument = () => {
    addToPortfolio.toggle();
  };

  const downloadErrorHandler = () => {
    error(t('Failed to export instruments'));
  };

  const exportToExcel = (items: SelectedEntities) => {
    const instrumentsIds = Object.values(items.items).map((item) => item[1][0]);
    return exportCoveredBonds({
      items: instrumentsIds,
      countries: countryCode === 'ALL' ? undefined : filters.countries,
      onError: downloadErrorHandler,
      selectedAll,
    });
  };

  const handleInstrumentsFiltersModalSubmit = (values: { [key: string]: any }) => {
    const {
      countries,
      industrySectors,
      ratingFrom,
      ratingTo,
      couponRangeFrom,
      couponRangeTo,
      issueVolumeFrom,
      issueVolumeTo,
      maturityDateFrom,
      maturityDateTo,
      issueVolumeCurrency,
      coveredBond,
      seniorityTypes,
    } = values;

    const newFilters = {
      ...(countries.length && { countries }),
      ...(industrySectors.length !== 0 && { industrySectors }),
      ...(values.ratingType !== null && { ratingType: [values.ratingType] }),
      ...(ratingFrom !== null && { ratingFrom }),
      ...(ratingTo !== null && { ratingTo }),
      ...(couponRangeFrom !== 0 && { couponRangeFrom }),
      ...(couponRangeTo !== 15 && { couponRangeTo }),
      ...(issueVolumeFrom !== 100000000 && { issueVolumeFrom }),
      ...(issueVolumeTo !== 1000000000 && { issueVolumeTo }),
      ...(maturityDateFrom !== null && { maturityDateFrom }),
      ...(maturityDateTo !== null && { maturityDateTo }),
      ...(issueVolumeCurrency !== null && { issueVolumeCurrency }),
      ...(coveredBond !== false && { coveredBond }),
      ...(seniorityTypes.length && { seniorityTypes }),
    };

    setFilters(newFilters);
    setShouldRefresh({ value: true, reason: refreshReasons.FILTERS_APPLIED });
    setInstrumentsFiltersModalOpen(false);
  };

  const initialColumns = useCallback(
    () => [
      {
        name: 'isin',
        label: t('COVERED_BONDS.ISIN'),
        options: {
          sort: true,
          customBodyRender: protectedRenderer,
        },
      },
      {
        name: 'name',
        label: t('COVERED_BONDS.NAME'),
        options: {
          sort: true,
          customBodyRender: nameRenderer,
        },
      },
      {
        name: 'issuerName',
        label: t('COVERED_BONDS.ENTITY_NAME'),
        options: {
          sort: true,
          customBodyRender: entityRenderer,
        },
      },
      {
        name: 'country',
        label: t('COVERED_BONDS.COUNTRY'),
        options: {
          sort: true,
          customBodyRender: countryRenderer,
        },
      },
      {
        name: 'seniorityType',
        label: t('COVERED_BONDS.SENIORITY_TYPE'),
        options: {
          sort: true,
          customBodyRender: protectedRenderer,
        },
      },
      {
        name: 'issueValue',
        label: t('COVERED_BONDS.INSTRUMENT_VALUE'),
        options: {
          sort: false,
          customBodyRender: valueRenderer,
        },
      },
      {
        name: 'ratingTypeName',
        label: t('COVERED_BONDS.TYPE'),
        options: {
          sort: false,
          customBodyRender: typeRenderer,
        },
      },
      {
        name: 'demandType',
        label: t('COVERED_BONDS.DEMAND_TYPE'),
        options: {
          sort: false,
          customBodyRender: protectedRenderer,
        },
      },
      {
        name: 'notchLabel',
        label: t('COVERED_BONDS.RATING'),
        options: {
          sort: true,
          customBodyRender: ratingRenderer,
        },
      },
      {
        name: 'outlook',
        label: t('COVERED_BONDS.OUTLOOK'),
        options: {
          sort: true,
          customBodyRender: protectedRenderer,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [singleSelect, multiSelect, currentSelect],
  );

  const getActions = (items: any) => [
    {
      text: 'Request selected',
      handler: requestRating,
      disabled: !currentSelect.count,
      hidden: true,
      data: currentSelect.itemIds,
    },
    {
      text: 'Add to portfolio',
      handler: copyInstrument,
      hidden: items.count === 0,
      disabled: selectedAll,
    },
    {
      text: 'Export to Excel',
      handler: () => exportToExcel(items),
      hidden: items.count === 0,
    },
  ];

  const { columns, pagination, onSortChange, onTableChange, refreshTable, isFiltered } = useTable({
    initialColumns,
    initialFilters,
  } as any);

  useEffect(() => {
    const ratingType = ratingTypes.CREDIT_RATING;

    const countries = buildCountryFilter(regions, countryCodes);
    const selectedCountry = countries.find((item: { iso: string }) => item.iso === countryCode);
    setAvailableCountries(countries);

    const modalFilters = getInitialSearchResultsModalFilters(
      ratingType,
      { countries: selectedCountry ? [selectedCountry] : [] },
      regions,
      industries,
    ) as { [x: string]: any; industries: any; countries: any };

    const initial = {
      countries: countryCode === 'ALL' ? [] : [countryCode],
      coveredBond: true,
      ratingFrom: 'D',
      ratingTo: 'AAA',
    };

    setInitialFilters(initial);
    setInitialModalFilters(modalFilters);

    setFilters(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions]);

  useEffect(() => {
    fetchCoveredBondsList(
      pagination.page,
      pagination.limit,
      pagination.sort && {
        sort: pagination.sort.sortColumn,
        dir: pagination.sort.direction,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, shouldRefresh, filters]);

  useEffect(() => {
    setTableData(prepareTableData(coveredBonds));
  }, [coveredBonds]);

  return (
    <Box className={classes.root}>
      <Datatable
        loading={updating || requestLoading}
        columns={columns}
        pagination={pagination}
        data={tableData}
        total={total}
        onSelectedChange={onSelectedChange}
        toolbarData={getActions(multiSelect)}
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        selectedAll={selectedAll}
        onSelectAllClick={onSelectAll}
        shouldRefresh={shouldRefresh}
        isFiltered={isFiltered}
        showFilters
        onApplyFiltersClick={() => setInstrumentsFiltersModalOpen(true)}
        removeShadow
        showSelectionExtension
      />
      <InstrumentsFiltersModal
        open={instrumentsFiltersModalOpen}
        onClose={toggleInstrumentsFiltersModal}
        onSubmit={handleInstrumentsFiltersModalSubmit}
        initialFilter={initialModalFilters}
        coveredBond
        countries={availableCountries}
        lockCountry={countryCode !== 'ALL'}
      />
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        itemsData={currentSelect}
        variant="add"
        itemType="INSTRUMENT"
      />
      <CreateNewPortfolioModal
        itemsData={currentSelect}
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemType="INSTRUMENT"
        actionVariant="add"
      />
    </Box>
  );
};
